import * as React from 'react';

import { cn } from '@/lib/utils';

type DataBlockVariant = 'barcode' | 'binary' | 'qr' | 'hex';

export interface DataBlockProps extends React.ComponentProps<'div'> {
    /** Type of data visualization */
    variant?: DataBlockVariant;
    /** Width of the block */
    width?: number;
    /** Height of the block */
    height?: number;
    /** Seed for deterministic generation */
    seed?: number;
}

// Generate deterministic random values
const seededRandom = (seed: number): (() => number) => {
    let s = seed;
    return () => {
        s = (s * 1103515245 + 12345) & 0x7fffffff;
        return s / 0x7fffffff;
    };
};

// Barcode pattern - vertical lines of varying widths
const BarcodePattern: React.FC<{
    width: number;
    height: number;
    seed: number;
}> = ({ width, height, seed }) => {
    const random = seededRandom(seed);
    const bars: { x: number; width: number }[] = [];
    let x = 0;

    while (x < width) {
        const barWidth = Math.floor(random() * 3) + 1;
        const gapWidth = Math.floor(random() * 3) + 1;

        if (random() > 0.3) {
            bars.push({ x, width: barWidth });
        }
        x += barWidth + gapWidth;
    }

    return (
        <svg
            width={width}
            height={height}
            viewBox={`0 0 ${width} ${height}`}
            className="opacity-30"
        >
            {bars.map((bar, i) => (
                <rect
                    key={i}
                    x={bar.x}
                    y={0}
                    width={bar.width}
                    height={height}
                    fill="currentColor"
                />
            ))}
        </svg>
    );
};

// Binary noise - random dot grid
const BinaryPattern: React.FC<{
    width: number;
    height: number;
    seed: number;
}> = ({ width, height, seed }) => {
    const random = seededRandom(seed);
    const cellSize = 2;
    const cols = Math.floor(width / cellSize);
    const rows = Math.floor(height / cellSize);
    const dots: { x: number; y: number }[] = [];

    for (let row = 0; row < rows; row++) {
        for (let col = 0; col < cols; col++) {
            if (random() > 0.6) {
                dots.push({
                    x: col * cellSize,
                    y: row * cellSize,
                });
            }
        }
    }

    return (
        <svg
            width={width}
            height={height}
            viewBox={`0 0 ${width} ${height}`}
            className="opacity-20"
        >
            {dots.map((dot, i) => (
                <rect
                    key={i}
                    x={dot.x}
                    y={dot.y}
                    width={1}
                    height={1}
                    fill="currentColor"
                />
            ))}
        </svg>
    );
};

// QR-like pattern - larger blocks in a grid
const QRPattern: React.FC<{ width: number; height: number; seed: number }> = ({
    width,
    height,
    seed,
}) => {
    const random = seededRandom(seed);
    const cellSize = 3;
    const cols = Math.floor(width / cellSize);
    const rows = Math.floor(height / cellSize);
    const blocks: { x: number; y: number }[] = [];

    // Corner markers (QR-like)
    const addCornerMarker = (startX: number, startY: number) => {
        for (let row = 0; row < 3; row++) {
            for (let col = 0; col < 3; col++) {
                if (row === 1 && col === 1) continue; // hollow center
                blocks.push({
                    x: (startX + col) * cellSize,
                    y: (startY + row) * cellSize,
                });
            }
        }
    };

    addCornerMarker(0, 0);
    addCornerMarker(cols - 3, 0);
    addCornerMarker(0, rows - 3);

    // Random fill
    for (let row = 0; row < rows; row++) {
        for (let col = 0; col < cols; col++) {
            // Skip corner areas
            const inCorner =
                (col < 4 && row < 4) ||
                (col >= cols - 4 && row < 4) ||
                (col < 4 && row >= rows - 4);
            if (!inCorner && random() > 0.65) {
                blocks.push({
                    x: col * cellSize,
                    y: row * cellSize,
                });
            }
        }
    }

    return (
        <svg
            width={width}
            height={height}
            viewBox={`0 0 ${width} ${height}`}
            className="opacity-15"
        >
            {blocks.map((block, i) => (
                <rect
                    key={i}
                    x={block.x}
                    y={block.y}
                    width={cellSize - 1}
                    height={cellSize - 1}
                    fill="currentColor"
                />
            ))}
        </svg>
    );
};

// Hex data display - fake hex values
const HexPattern: React.FC<{ width: number; height: number; seed: number }> = ({
    height,
    seed,
}) => {
    const random = seededRandom(seed);
    const lines = Math.floor(height / 10);
    const hexLines: string[] = [];

    for (let i = 0; i < lines; i++) {
        let line = '';
        const chars = Math.floor(random() * 6) + 4;
        for (let j = 0; j < chars; j++) {
            line += Math.floor(random() * 16)
                .toString(16)
                .toUpperCase();
        }
        hexLines.push(line);
    }

    return (
        <div className="flex flex-col gap-px font-mono text-[6px] leading-none opacity-25">
            {hexLines.map((line, i) => (
                <span key={i}>{line}</span>
            ))}
        </div>
    );
};

const DataBlock = React.forwardRef<HTMLDivElement, DataBlockProps>(
    (
        {
            className,
            variant = 'barcode',
            width = 40,
            height = 20,
            seed = 42,
            ...props
        },
        ref,
    ) => {
        const PatternComponent = {
            barcode: BarcodePattern,
            binary: BinaryPattern,
            qr: QRPattern,
            hex: HexPattern,
        }[variant];

        return (
            <div
                ref={ref}
                className={cn(
                    'pointer-events-none select-none text-muted-foreground',
                    className,
                )}
                style={{ width, height }}
                {...props}
            >
                <PatternComponent width={width} height={height} seed={seed} />
            </div>
        );
    },
);

DataBlock.displayName = 'DataBlock';

// Pre-configured variants
const Barcode = React.forwardRef<
    HTMLDivElement,
    Omit<DataBlockProps, 'variant'>
>((props, ref) => <DataBlock ref={ref} variant="barcode" {...props} />);
Barcode.displayName = 'Barcode';

const BinaryNoise = React.forwardRef<
    HTMLDivElement,
    Omit<DataBlockProps, 'variant'>
>((props, ref) => <DataBlock ref={ref} variant="binary" {...props} />);
BinaryNoise.displayName = 'BinaryNoise';

const QRBlock = React.forwardRef<
    HTMLDivElement,
    Omit<DataBlockProps, 'variant'>
>((props, ref) => (
    <DataBlock ref={ref} variant="qr" width={30} height={30} {...props} />
));
QRBlock.displayName = 'QRBlock';

const HexBlock = React.forwardRef<
    HTMLDivElement,
    Omit<DataBlockProps, 'variant'>
>((props, ref) => <DataBlock ref={ref} variant="hex" {...props} />);
HexBlock.displayName = 'HexBlock';

export { DataBlock, Barcode, BinaryNoise, QRBlock, HexBlock };
